import { Badge, Button, Group, MultiSelect, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import BasePage from '../BasePage';
import User from '../../interfaces/User';
import { coreApi } from '../../utils/CoreAPI';
import ResponsiveTable from '../../components/ResponsiveTable';
import { DataTableColumn } from 'mantine-datatable';
import { useForm } from '@mantine/form';
import { closeAllDialogs, openDialogBox } from '../../components/DialogBox';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconX } from '@tabler/icons-react';

const EditRolesForm = ({ user, roles }: { user: User, roles: string[] }) => {
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            oid: user.oid,
            name: user.name,
            email: user.email,
            roles: user.roles,
            permissions: user.permissions
        },
    });

    const handleRoleEditing = async (data: any) => {
        const response = await coreApi.patch(`/users?id=${user.oid}`, data);

        if (response.status !== 204) {
            notifications.show({
                title: response.error,
                message: response.data['non_field_errors'][0] ?? "Something went wrong",
                withBorder: true,
                withCloseButton: true,
                autoClose: false,
                icon: <IconX size={"1.125rem"} />,
                color: "red"
            });

            return;
        }

        window.location.reload();
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleRoleEditing(values))}>
            <Stack>
                <Tooltip label="Managed by Entra" position="right" withArrow arrowSize={10}>
                    <TextInput
                        disabled
                        withAsterisk
                        label="User ID"
                        key={form.key('oid')}
                        {...form.getInputProps('oid')}
                    />
                </Tooltip>

                <Tooltip label="Managed by Entra" position="right" withArrow arrowSize={10}>
                    <TextInput
                        disabled
                        withAsterisk
                        label="Name"
                        key={form.key('name')}
                        {...form.getInputProps('name')}
                    />
                </Tooltip>

                <Tooltip label="Managed by Entra" position="right" withArrow arrowSize={10}>
                    <TextInput
                        disabled
                        withAsterisk
                        label="E-Mail"
                        key={form.key('email')}
                        {...form.getInputProps('email')}
                    />
                </Tooltip>

                <MultiSelect
                    withAsterisk
                    hidePickedOptions
                    clearSectionMode="clear"
                    label="Assigned Roles"
                    placeholder="Pick role"
                    key={form.key('roles')}
                    {...form.getInputProps('roles')}
                    data={roles}
                />


                <Group justify="right" gap={6}>
                    <Button onClick={closeAllDialogs}  radius={12} h={34}>Cancel</Button>
                    <Button className="unify-btn-primary" type="submit"  radius={12} h={34}>Save changes</Button>
                </Group>
            </Stack>
        </form>
    );
};

function SecurityRolesPage() {
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<string[]>([]);

    const getUsers = async () => {
        const response = await coreApi.get('users');

        setUsers(response.data);
    };

    const getRoles = async () => {
        const response = await coreApi.get('admin/auth/roles');
    
        const roles = response.data.map(role => ({
            value: role.name,
            label: role.name,
            disabled: role.name === "User",
        }));
    
        setRoles(roles);
    };

    const handleUserViewDetails = (user: User) => {
        openDialogBox({ title: "Edit Roles", children: <EditRolesForm user={user} roles={roles} /> })
    };

    useEffect(() => {
        getUsers();
        getRoles();
    }, []);

    const columns: DataTableColumn<User>[] = [
        { accessor: "oid", title: "User ID", sortable: true },
        { accessor: "name", title: "Name", sortable: true },
        { accessor: "email", title: "E-Mail", sortable: true },
        {
            accessor: "roles", title: "Assigned Roles", sortable: true, render: (record) => (
                <Group gap={6} align='center'>
                    {record.roles.map((role) => {
                        const isAdmin = role === "Admin";
                        const badgeContent = (
                            <Badge
                                key={role}
                                variant="light"
                                size="lg"
                                color={isAdmin ? "red" : "green"}
                                leftSection={role === "Admin" && <IconAlertTriangle size="0.8rem" />}
                            >
                                <Text fz="0.7rem">{role}</Text>
                            </Badge>
                        );

                        return isAdmin ? (
                            <Tooltip
                                label="Please consider using a less permissive role."
                                position="bottom"
                                withArrow
                                arrowSize={10}
                            >
                                {badgeContent}
                            </Tooltip>
                        ) : badgeContent;
                    })}
                </Group>
            )
        },
        { accessor: "permissions", title: "Assigned Permissions", sortable: true },
    ];

    return (
        <BasePage
            breadcrumbs={[{ label: "Roles" }]}
            fullWidth
        >
            <ResponsiveTable
                data={users}
                columns={columns}
                gridRowId={(row) => row.oid}
                onRowClick={(row) => { handleUserViewDetails(row) }}
                gridSortModel={{
                    columnAccessor: "displayName",
                    direction: "asc",
                }}
                selectionColumnStyle={{ display: 'none' }}
            />
        </BasePage>
    )
};

export default SecurityRolesPage;